import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Car, Navigation, Star } from 'lucide-react';

const LocationPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // RC Awards San Diego coordinates
  const position = { lat: 32.754847, lng: -117.137303 };

  const businessHours = [
    { day: 'Monday', hours: '9:00 AM - 6:00 PM' },
    { day: 'Tuesday', hours: '9:00 AM - 6:00 PM' },
    { day: 'Wednesday', hours: '9:00 AM - 6:00 PM' },
    { day: 'Thursday', hours: '9:00 AM - 6:00 PM' },
    { day: 'Friday', hours: '9:00 AM - 6:00 PM' },
    { day: 'Saturday', hours: '10:00 AM - 1:00 PM' },
    { day: 'Sunday', hours: 'Closed' }
  ];

  const nearbyAreas = [
    { name: 'North Park', distance: '5 min' },
    { name: 'Hillcrest', distance: '7 min' },
    { name: 'University Heights', distance: '3 min' },
    { name: 'Normal Heights', distance: '5 min' },
    { name: 'City Heights', distance: '5 min' },
    { name: 'Mission Valley', distance: '10 min' },
    { name: 'La Mesa', distance: '12 min' },
    { name: 'Downtown San Diego', distance: '15 min' }
  ];

  const directions = [
    {
      from: 'From I-8 East',
      steps: 'Exit at 70th Street, head south, turn right onto El Cajon Blvd. We\'re on the right side between Arizona St and 30th St.'
    },
    {
      from: 'From I-8 West',
      steps: 'Exit at Fairmount Ave, head south, turn left onto El Cajon Blvd. Continue west for about 1 mile. We\'re on the left side.'
    },
    {
      from: 'From Downtown San Diego',
      steps: 'Take Park Blvd north, turn right onto University Ave, then left onto 30th St. Turn right onto El Cajon Blvd.'
    },
    {
      from: 'From La Mesa / El Cajon',
      steps: 'Take El Cajon Blvd west. We\'re located between Arizona St and 30th St on the right side.'
    }
  ];

  return (
    <div data-testid="location-page" className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-[#1a365d] text-white pt-32 pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="text-sm uppercase tracking-widest text-[#D4AF37] mb-3">Visit Our San Diego Showroom</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              RC Awards Location
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Visit our trophy shop on El Cajon Blvd in San Diego. See our full selection of trophies, awards, and promotional products in person.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Address & Contact Info */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-10">
          {/* Left - Address & Hours */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="bg-gray-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-[#1a365d] mb-6">Visit Our Showroom</h2>
              
              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#D4AF37]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1a365d] mb-1">Address</h3>
                    <p className="text-gray-600">
                      2509 El Cajon Blvd #A<br />
                      San Diego, CA 92104<br />
                      United States
                    </p>
                    <a 
                      href="https://www.google.com/maps/place/RC+Awards/@32.754847,-117.137303,17z"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#D4AF37] text-sm font-medium hover:underline mt-2 inline-block"
                    >
                      Get Directions →
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#D4AF37]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1a365d] mb-1">Phone</h3>
                    <a href="tel:6196401633" className="text-gray-600 hover:text-[#D4AF37]">
                      (619) 640-1633
                    </a>
                    <p className="text-sm text-gray-500 mt-1">Call or Text</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#D4AF37]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1a365d] mb-1">Email</h3>
                    <a href="mailto:RCAwards@Aol.com" className="text-gray-600 hover:text-[#D4AF37]">
                      RCAwards@Aol.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Business Hours */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="text-[#D4AF37]" size={24} />
                <h2 className="text-2xl font-bold text-[#1a365d]">Business Hours</h2>
              </div>
              
              <div className="space-y-3">
                {businessHours.map((item, index) => (
                  <div 
                    key={index}
                    className={`flex justify-between py-2 ${index !== businessHours.length - 1 ? 'border-b border-gray-200' : ''}`}
                  >
                    <span className="font-medium text-[#1a365d]">{item.day}</span>
                    <span className={item.hours === 'Closed' ? 'text-red-500' : 'text-gray-600'}>
                      {item.hours}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-[#D4AF37]/10 rounded-lg">
                <p className="text-sm text-[#1a365d]">
                  <strong>Rush Orders?</strong> Call ahead and we'll do our best to accommodate same-day or next-day service!
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Google Map Embed */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="rounded-2xl overflow-hidden shadow-lg"
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3356.8!2d-117.137303!3d32.754847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80d954d3a5d9b36d%3A0x2ae1885968717e70!2sRC%20Awards!5e0!3m2!1sen!2sus!4v1234567890"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="RC Awards Location Map"
            className="w-full"
          ></iframe>
        </motion.div>
      </section>

      {/* Driving Directions */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl font-bold text-[#1a365d] mb-3">Driving Directions</h2>
            <p className="text-gray-600">Easy access from anywhere in San Diego County</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {directions.map((dir, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-3">
                  <Car className="text-[#D4AF37]" size={20} />
                  <h3 className="font-semibold text-[#1a365d]">{dir.from}</h3>
                </div>
                <p className="text-gray-600 text-sm">{dir.steps}</p>
              </motion.div>
            ))}
          </div>

          {/* Parking Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mt-8 bg-[#1a365d] text-white rounded-xl p-6 text-center"
          >
            <h3 className="font-semibold text-lg mb-2">Free Parking Available</h3>
            <p className="text-white/80 text-sm">
              Convenient free parking in front of our store and additional street parking on El Cajon Blvd.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Areas We Serve */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl font-bold text-[#1a365d] mb-3">Areas We Serve</h2>
            <p className="text-gray-600">Proudly serving all of San Diego County</p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {nearbyAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-lg p-4 text-center hover:bg-[#D4AF37]/10 transition-colors"
              >
                <p className="font-medium text-[#1a365d]">{area.name}</p>
                <p className="text-sm text-gray-500">{area.distance} drive</p>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center text-gray-500 mt-8"
          >
            Also serving: Chula Vista, National City, El Cajon, Santee, Poway, Escondido, Coronado, La Jolla, Pacific Beach, and all surrounding areas.
          </motion.p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1a365d] py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={20} className="fill-[#D4AF37] text-[#D4AF37]" />
              ))}
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Visit San Diego's #1 Rated Trophy Shop</h2>
            <p className="text-white/80 mb-8">
              Stop by our showroom to see our full selection of trophies, plaques, awards, and promotional products. Our friendly team is ready to help!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="tel:6196401633"
                className="bg-[#D4AF37] text-[#1a365d] px-8 py-3 rounded-full font-semibold hover:bg-[#c9a432] transition-colors flex items-center gap-2"
              >
                <Phone size={18} />
                (619) 640-1633
              </a>
              <a
                href="https://www.google.com/maps/dir//RC+Awards,+2509+El+Cajon+Blvd,+San+Diego,+CA+92104"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 text-white px-8 py-3 rounded-full font-semibold hover:bg-white/20 transition-colors flex items-center gap-2"
              >
                <Navigation size={18} />
                Get Directions
              </a>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-[#1a365d] transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LocationPage;
